import { memo, useEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { ButtonPrimary } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { GenericInspectorPanelContainer } from "../inspector-panels/GenericInspectorPanel"
import { GenericInspectorPanelHeaderTopRow } from "../inspector-panels/headers/GenericInspectorPanelHeader"
import { GenericInspectorPanelFooterContainer } from "../inspector-panels/footers/GenericInspectorPanelFooter"
import classNames from "classnames"
import {
    deactivateOrthoComparison,
    setOrthoComparisonDirection,
    setOrthoComparisonOpacity,
    selectOrthoComparison,
    type ComparisonDirection,
} from "plugins/Orthomosaic/OrthoComparisonSlice"
import { useDebounceDispatch } from "tools/store"
import { InspectorPanelAccordionSlider } from "../accordion/InspectorPanelAccordionSlider"

type DirectionDef = {
    value: ComparisonDirection
    Icon: Icons.IconComponent
    title: string
    iconClassName?: string
}

const DIRECTIONS: DirectionDef[] = [
    { value: "left", Icon: Icons.ArrowLeft, title: "Show left" },
    { value: "right", Icon: Icons.ArrowRight, title: "Show right" },
    { value: "up", Icon: Icons.ArrowUp, title: "Show top" },
    {
        value: "down",
        Icon: Icons.ArrowUp, // ArrowUp rotated 180° — no separate ArrowDown icon exists
        title: "Show bottom",
        iconClassName: "rotate-180",
    },
]

function DirectionButton({
    def,
    isSelected,
    onClick,
    isFirst,
    isLast,
}: {
    def: DirectionDef
    isSelected: boolean
    onClick: () => void
    isFirst?: boolean
    isLast?: boolean
}) {
    return (
        <button
            title={def.title}
            className={classNames(
                "h-9 flex grow justify-center items-center cursor-pointer select-none border",
                {
                    "text-gray-0 border-gray-300 bg-gray-700": isSelected,
                    "border-gray-700 text-gray-400 hover:text-gray-300 hover:border-gray-500 active:text-gray-0 active:border-gray-300":
                        !isSelected,
                    "rounded-l-[8px]": isFirst,
                    "rounded-r-[8px]": isLast,
                }
            )}
            onClick={onClick}
        >
            <Icons.SmallIcon
                Icon={def.Icon}
                iconClassName={classNames(
                    isSelected ? "text-blue-500" : "text-gray-400",
                    def.iconClassName
                )}
            />
        </button>
    )
}

const InspectorPanelOrthoComparisonPanel = memo(() => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(250)
    const { direction, comparisonOpacity } = useAppSelector(selectOrthoComparison)
    const explicitlyDeactivatedRef = useRef(false)

    // Deactivate comparison when panel is hidden (user clicks away, selection changes, etc.)
    // Guard prevents a double-dispatch when the Done button already deactivated explicitly.
    useEffect(() => {
        return () => {
            if (!explicitlyDeactivatedRef.current) dispatch(deactivateOrthoComparison())
        }
    }, [dispatch])

    return (
        <GenericInspectorPanelContainer>
            <div className="flex flex-col min-h-0 shrink">
                <div className="px-4 pt-4 pb-3 border-b border-b-gray-700">
                    <GenericInspectorPanelHeaderTopRow
                        typeTitle="Comparison Slider"
                        Icon={Icons.RatioBar}
                    />
                </div>

                <ScrollableArea shouldPreventScrollbarShifting>
                    <div className="flex flex-col px-4 py-4 gap-4">
                        {/* Opacity */}
                        <InspectorPanelAccordionSlider
                            title={<small className="xs text-gray-300 select-none">Opacity</small>}
                            resetValue={100}
                            existingSliderValue={comparisonOpacity}
                            minSliderValue={1}
                            maxSliderValue={100}
                            onValueChanged={(val) =>
                                debounceDispatch(setOrthoComparisonOpacity(val))
                            }
                        />

                        {/* Direction */}
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center h-5">
                                <small className="xs text-gray-300 select-none">Direction</small>
                            </div>
                            <div className="flex w-full">
                                {DIRECTIONS.map((def, i) => (
                                    <DirectionButton
                                        key={def.value}
                                        def={def}
                                        isSelected={direction === def.value}
                                        onClick={() =>
                                            dispatch(setOrthoComparisonDirection(def.value))
                                        }
                                        isFirst={i === 0}
                                        isLast={i === DIRECTIONS.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </ScrollableArea>

                <GenericInspectorPanelFooterContainer>
                    <div className="flex justify-end">
                        <ButtonPrimary
                            title="Done"
                            RightIcon={Icons.Checkmark}
                            onClick={() => {
                                explicitlyDeactivatedRef.current = true
                                dispatch(deactivateOrthoComparison())
                            }}
                        />
                    </div>
                </GenericInspectorPanelFooterContainer>
            </div>
        </GenericInspectorPanelContainer>
    )
})
InspectorPanelOrthoComparisonPanel.displayName = "InspectorPanelOrthoComparisonPanel"
export { InspectorPanelOrthoComparisonPanel }
